import { useState, useEffect, useDeferredValue } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTasks, useProjects } from '@/hooks/useTasks.ts';
import { TaskRow } from '@/components/TaskRow.tsx';
import { TaskDrawer } from '@/components/TaskDrawer.tsx';
import { PageHeader, EmptyState, Input } from '@/components/ui.tsx';
import type { TaskFilter } from '@shared/types';

/**
 * Full-text search across titles and notes.
 *
 * The query lives in the URL so a search is bookmarkable and survives a
 * refresh — useful when you find something and want to come back to it.
 */
export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskId = searchParams.get('task');
  const urlQuery = searchParams.get('q') ?? '';

  const [input, setInput] = useState(urlQuery);

  // Keeps the field responsive while a slower query renders behind it.
  const deferred = useDeferredValue(input);

  // Push the query into the URL, but not on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (input.trim()) next.set('q', input);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const trimmed = deferred.trim();

  // Searching includes archived and completed work: if you are looking for
  // something by name, "I finished it last month" is a likely reason you
  // cannot find it in the normal views.
  const filter: TaskFilter = { q: trimmed, include_archived: true };

  const { data: tasks, isFetching } = useTasks(filter);
  const { data: projects } = useProjects();
  const projectNames = new Map((projects ?? []).map((p) => [p.id, p.name]));

  function openTask(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('task', id);
      return next;
    });
  }

  function closeTask() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }

  const results = trimmed ? (tasks ?? []) : [];

  return (
    <div className="mx-auto w-full max-w-[1120px] px-5 py-8 sm:px-8">
      <PageHeader
        title="Search"
        subtitle="Titles and notes, including completed and archived work"
        {...(trimmed && tasks ? { count: `${tasks.length} found` } : {})}
      />

      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        autoFocus
        placeholder="Search tasks"
        aria-label="Search tasks"
        className="mb-6 py-2.5"
      />

      {!trimmed && (
        <EmptyState
          title="Search everything you have written down."
          body="Titles and note bodies, completed and archived included. Partial words match, so “recon” finds “reconciliation”."
        />
      )}

      {trimmed && results.length === 0 && !isFetching && (
        <EmptyState
          title={`Nothing matches “${trimmed}”.`}
          body="Try fewer words, or a word from the notes rather than the title."
        />
      )}

      {results.length > 0 && (
        <>
          <ul className="border-t border-line">
            {results.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onOpen={openTask}
                projectName={task.project_id ? (projectNames.get(task.project_id) ?? null) : null}
              />
            ))}
          </ul>
        </>
      )}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={closeTask} />}
    </div>
  );
}
